import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  Users,
  Plus,
  Search,
  Paperclip,
  Smile,
  UserPlus,
  Image,
  File,
  CheckCheck,
  RefreshCw,
  Bell,
  X,
  Reply,
  Eye,
  ArrowLeft,
  Pencil,
  Trash2
} from 'lucide-react';

// Notification sound - Loud, clear chime for headphone users
// Uses Web Audio API for an attention-grabbing notification that cuts through call audio
const createNotificationSound = () => {
  let audioContext = null;
  
  const getAudioContext = () => {
    if (!audioContext || audioContext.state === 'closed') {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContext;
  };
  
  const playNotification = async () => {
    try {
      const ctx = getAudioContext();
      
      // Resume audio context if suspended (required due to browser autoplay policy)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Create master gain for overall volume control - MAXIMUM VOLUME
      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.value = 1.0; // Full volume
      
      // Play attention-grabbing double ding pattern
      const pattern = [
        { freq: 1047, delay: 0 },      // C6 - first ding
        { freq: 1319, delay: 0.08 },   // E6
        { freq: 1568, delay: 0.16 },   // G6
        { freq: 1047, delay: 0.35 },   // C6 - second ding (repeat)
        { freq: 1319, delay: 0.43 },   // E6
        { freq: 1568, delay: 0.51 },   // G6
      ];
      
      pattern.forEach(({ freq, delay }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.value = freq;
        osc.type = 'sine';
        
        // Sharp attack, medium decay for clear notification
        gain.gain.setValueAtTime(0, now + delay);
        gain.gain.linearRampToValueAtTime(0.7, now + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.25);
        
        osc.start(now + delay);
        osc.stop(now + delay + 0.25);
      });
      
      console.log('🔔 Chat notification sound played successfully');
      return true;
    } catch (e) {
      console.error('Failed to play notification:', e);
      return false;
    }
  };
  
  return { play: playNotification };
};

// Fallback using HTML5 Audio with a proper notification sound
const createFallbackSound = () => {
  // Create a beep sound using data URI
  const beepSound = new Audio('data:audio/wav;base64,UklGRl9vT19teleVFN/+LRtY9fN0KP1+bJoXtXVYDM5tylhF5TVnm/5uSqjGdcXXy76t+qj2pfXXi449yrkmpfYHm85uCqjWlgXXi85t+qjmlhX3m75d+qjmliXnq75d6pjmliXnq75d6pjmliXni65d6pjmliXnm65d6pjmliXni65t6pjmljXnm65d6pjmhiXnm65d6pjmhjXnm65N6pjmhiXnq65d2pjmhiXnq65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhiX3q65d2pjmhjX3m65d2oj2hjX3m65d2oj2ljX3m65N2nj2ljYHm549yoj2pjYHq549ynj2pjYHq549ynj2pjYHq549ynj2pjYHq549ynj2pjYHu549unj2tjYHq549ynkGpjYXq549umkGpjYXu54dunj2tjYXu549umkGtkYXu549ulkGtkYXu549ulkGtkYXu549ulkGtkYXu549ulkGtkYXy549qkkGxkYny549qkkGxkYny549qkkGxkYn2549qjkGxkYn2549qjkGxlYn2449qjkG1lYn2449qjkG1lY32449mjkG1lY32449mjkG5lY32449mjkG5lY32449mjkG5lY32449mjkG5lY32449mjkG5lY32449mjkG5mY32449ijkW5mY36449ijkW5mY36449ijkW5mY36449ijkW9mZH6449ij');
  beepSound.volume = 1.0;
  return beepSound;
};

// Initialize notification sound lazily
let notificationSound = null;
let fallbackSound = null;

const getNotificationSound = () => {
  if (!notificationSound) {
    try {
      notificationSound = createNotificationSound();
    } catch (e) {
      console.log('Web Audio API not available, using fallback');
    }
  }
  return notificationSound;
};

// Play notification with error handling and fallback
const playNotificationSound = async () => {
  try {
    const sound = getNotificationSound();
    if (sound) {
      await sound.play();
    } else {
      // Use fallback
      if (!fallbackSound) {
        fallbackSound = createFallbackSound();
      }
      fallbackSound.currentTime = 0;
      await fallbackSound.play();
    }
  } catch (e) {
    console.log('Notification sound could not play:', e.message);
  }
};

// Emoji categories
const EMOJI_CATEGORIES = {
  'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷'],
  'Gestures': ['👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏', '🙌', '🤲', '🤝', '🙏', '✍️', '💪', '🦵', '🦶'],
  'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '💕', '💞', '💓', '💗', '💖', '💘', '💝'],
  'Reactions': ['🔥', '💯', '✅', '❌', '⭐', '🌟', '💫', '✨', '🎉', '🎊', '🏆', '🥇', '🥈', '🥉', '🎯', '💡', '📌', '📍', '🚀', '💼', '📈', '📉', '💰', '💵']
};

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '👏', '🔥', '💯', '✅', '🎉'];

// Helper functions moved outside component to prevent recreation
const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Memoized Conversation Item - prevents re-render when other conversations change
const ConversationItem = memo(({ chat, isSelected, onClick }) => {
  const isOnline = chat.type === 'direct' && chat.participants_info?.[0]?.is_online;
  const chatName = chat.type === 'group' ? chat.name : chat.participants_info?.[0]?.name || 'Unknown';
  
  return (
    <div 
      onClick={onClick} 
      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
        isSelected 
          ? 'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800' 
          : 'hover:bg-muted'
      }`}
    >
      <div className="relative">
        <Avatar className="w-12 h-12">
          <AvatarFallback className={`${chat.type === 'group' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-indigo-400 to-purple-500'} text-white`}>
            {chat.type === 'group' ? <Users className="w-5 h-5" /> : getInitials(chatName)}
          </AvatarFallback>
        </Avatar>
        {chat.type === 'direct' && (
          <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-800 ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
        )}
        {chat.unread_count > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {chat.unread_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{chatName}</p>
            {chat.type === 'direct' && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isOnline ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{formatTime(chat.last_message_at)}</span>
        </div>
        <p className="text-sm text-muted-foreground truncate">{chat.last_message || 'No messages yet'}</p>
      </div>
    </div>
  );
});
ConversationItem.displayName = 'ConversationItem';

// Memoized Message Bubble - only re-renders when message changes
const MessageBubble = memo(({ msg, isOwn, showAvatar, onReply, onReaction, activeReactionMsg, setActiveReactionMsg, backendUrl, isAdmin, onEdit, onDelete }) => {
  const readReceiptText = useMemo(() => {
    if (!isOwn || !msg.seen_by || msg.seen_by.length === 0) return null;
    const seenByOthers = msg.seen_by.filter(s => s.user_id !== msg.sender_id);
    if (seenByOthers.length === 0) return null;
    if (seenByOthers.length === 1) return `Seen by ${seenByOthers[0].user_name}`;
    if (seenByOthers.length === 2) return `Seen by ${seenByOthers[0].user_name} and ${seenByOthers[1].user_name}`;
    return `Seen by ${seenByOthers.length} people`;
  }, [isOwn, msg.seen_by, msg.sender_id]);

  if (msg.deleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="text-sm text-muted-foreground italic bg-muted/50 px-4 py-2 rounded-lg">Message deleted</div>
      </div>
    );
  }
  
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}>
      <div className={`flex items-end gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && showAvatar && (
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs">
              {getInitials(msg.sender_name)}
            </AvatarFallback>
          </Avatar>
        )}
        {!isOwn && !showAvatar && <div className="w-8" />}
        
        <div className="relative">
          {!isOwn && showAvatar && <p className="text-xs text-muted-foreground mb-1 ml-1">{msg.sender_name}</p>}
          
          {msg.reply_to && (
            <div className={`mb-1 px-3 py-1.5 rounded-lg text-xs border-l-2 ${isOwn ? 'bg-white/10 border-white/30 text-white/80' : 'bg-muted border-primary'}`}>
              <p className="font-medium text-xs opacity-75">{msg.reply_to.sender_name}</p>
              <p className="truncate max-w-[200px]">{msg.reply_to.content}</p>
            </div>
          )}
          
          <div className={`px-4 py-2 rounded-2xl ${isOwn ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-br-md' : 'bg-muted rounded-bl-md'}`}>
            {msg.file_url && (
              <a href={`${backendUrl}${msg.file_url}`} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 mb-1 text-sm ${isOwn ? 'text-white/90' : 'text-primary'} hover:underline`}>
                {msg.file_type?.startsWith('image/') ? <Image className="w-4 h-4" /> : <File className="w-4 h-4" />}
                {msg.file_name}
              </a>
            )}
            <p className="text-sm whitespace-pre-wrap break-words" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {msg.content.split(/(@\w+)/g).map((part, i) => 
                part.startsWith('@') ? (
                  <span key={i} className={`font-medium ${isOwn ? 'text-yellow-200' : 'text-primary'}`}>{part}</span>
                ) : part
              )}
            </p>
            <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
              {msg.edited_at && <span className="text-[10px] italic">edited</span>}
              <span className="text-[10px]">{formatTime(msg.created_at)}</span>
              {isOwn && (msg.is_sending ? 
                <RefreshCw className="w-3 h-3 animate-spin" /> : 
                <CheckCheck className={`w-3 h-3 ${readReceiptText ? 'text-sky-300' : ''}`} />
              )}
            </div>
          </div>
          
          {readReceiptText && (
            <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <Eye className="w-3 h-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">{readReceiptText}</span>
            </div>
          )}
          
          {msg.reactions?.length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {msg.reactions.map((r, i) => (
                <span key={i} className="text-sm bg-muted rounded-full px-2 py-0.5 border shadow-sm" title={r.user_name}>{r.emoji}</span>
              ))}
            </div>
          )}
          
          <div className={`absolute ${isOwn ? 'right-full mr-1' : 'left-full ml-1'} top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm rounded-full shadow-sm`}>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onReply(msg)} title="Reply">
              <Reply className="w-4 h-4" />
            </Button>
            <Popover open={activeReactionMsg === msg.id} onOpenChange={(open) => setActiveReactionMsg(open ? msg.id : null)}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7"><Smile className="w-4 h-4" /></Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" side={isOwn ? 'left' : 'right'}>
                <div className="flex gap-1">
                  {QUICK_REACTIONS.map(emoji => (
                    <Button key={emoji} variant="ghost" size="sm" className="text-lg p-1 h-8 w-8 hover:bg-muted" onClick={() => onReaction(msg.id, emoji)}>{emoji}</Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {isAdmin && !msg.is_sending && (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-500" onClick={() => onEdit(msg)} title="Edit (Admin)" data-testid={`edit-message-${msg.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(msg)} title="Delete (Admin)" data-testid={`delete-message-${msg.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
MessageBubble.displayName = 'MessageBubble';

const ChatPage = () => {
  const { user, api, isAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState('Smileys');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeReactionMsg, setActiveReactionMsg] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({}); // {chat_id: [user_ids...]}
  const [replyingTo, setReplyingTo] = useState(null); // Message being replied to
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const inputRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Unlock audio context on first user interaction (required by browsers)
  useEffect(() => {
    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      // Initialize the audio context by playing a silent sound
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0; // Silent
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.001);
        console.log('🔊 Audio context unlocked for notifications');
      } catch (e) {
        console.log('Audio unlock failed:', e);
      }
    };

    // Listen for any user interaction
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // WebSocket connection for real-time messages
  useEffect(() => {
    const token = localStorage.getItem('omnilink_token');
    if (!token) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat/${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      // Start keep-alive ping
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_message') {
        // Only play sound for messages from OTHER users (not your own)
        const currentUserId = JSON.parse(atob(localStorage.getItem('omnilink_token')?.split('.')[1] || '{}')).sub;
        const isOwnMessage = data.message.sender_id === currentUserId;
        
        if (!isOwnMessage) {
          // ALWAYS play notification sound for every incoming message
          console.log('🔔 New message received, playing sound...');
          playNotificationSound();
          
          // Show browser push notification if permission granted and tab not focused
          if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
            const notification = new Notification(`New message from ${data.sender_name}`, {
              body: data.message.content?.substring(0, 100) || 'New message',
              icon: '/favicon.ico',
              tag: `chat-${data.chat_id}-${Date.now()}`, // Unique tag for each message
              requireInteraction: false
            });
            
            notification.onclick = () => {
              window.focus();
              notification.close();
            };
            
            // Auto close after 5 seconds
            setTimeout(() => notification.close(), 5000);
          }
          
          // Show in-app toast notification for messages not in current chat
          if (selectedChat?.id !== data.chat_id) {
            toast.custom((t) => (
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-in">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{data.sender_name}</p>
                  <p className="text-xs text-white/80 truncate max-w-[200px]">{data.message.content}</p>
                </div>
                <Bell className="w-5 h-5 animate-bounce" />
              </div>
            ), { duration: 4000 });
          }
        }
        
        // Update messages immediately if in the same chat
        if (selectedChat?.id === data.chat_id) {
          setMessages(prev => [...prev, data.message]);
          scrollToBottom();
          // Clear typing indicator when message received
          setTypingUsers(prev => {
            const updated = { ...prev };
            if (updated[data.chat_id]) {
              updated[data.chat_id] = updated[data.chat_id].filter(id => id !== data.message.sender_id);
            }
            return updated;
          });
        }
        // Update conversation list (but debounced - not on every single message)
        // The polling interval will handle this
      }
      
      // Handle admin message edit (real-time for all participants)
      if (data.type === 'message_edited') {
        if (selectedChat?.id === data.chat_id) {
          setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, content: data.content, edited_at: data.edited_at } : m));
        }
        fetchConversations();
      }
      
      // Handle admin message deletion (real-time for all participants)
      if (data.type === 'message_deleted') {
        if (selectedChat?.id === data.chat_id) {
          setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, deleted: true } : m));
        }
        fetchConversations();
      }
      
      // Handle user online/offline status
      if (data.type === 'user_status') {
        setOnlineUsers(prev => {
          const updated = new Set(prev);
          if (data.is_online) {
            updated.add(data.user_id);
          } else {
            updated.delete(data.user_id);
          }
          return updated;
        });
        // Don't fetch conversations here - let polling handle it
      }
      
      // Handle typing indicator
      if (data.type === 'typing') {
        setTypingUsers(prev => {
          const updated = { ...prev };
          if (!updated[data.chat_id]) {
            updated[data.chat_id] = [];
          }
          
          if (data.is_typing) {
            if (!updated[data.chat_id].includes(data.user_id)) {
              updated[data.chat_id] = [...updated[data.chat_id], data.user_id];
            }
          } else {
            updated[data.chat_id] = updated[data.chat_id].filter(id => id !== data.user_id);
          }
          return updated;
        });
      }
      
      // Handle broadcast notifications
      if (data.type === 'broadcast') {
        window.dispatchEvent(new CustomEvent('broadcast-notification', { detail: data }));
      }
      
      // Handle motivation notifications
      if (data.type === 'motivation') {
        window.dispatchEvent(new CustomEvent('motivation-notification', { detail: data }));
      }
    };
    
    ws.onclose = (event) => {
      console.log('WebSocket disconnected', event.code);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      
      // Auto-reconnect after 3 seconds (unless intentional close)
      if (event.code !== 1000) {
        setTimeout(() => {
          console.log('Attempting WebSocket reconnection...');
          // Trigger reconnect by updating a dependency
          setMessages(prev => [...prev]);
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
    
    return () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      ws.close();
    };
  }, [selectedChat?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/chat/users');
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const response = await api.get(`/chat/${chatId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchConversations(), fetchAvailableUsers()]);
      setLoading(false);
    };
    init();
    // Poll conversations every 10 seconds (reduced load - WebSocket handles real-time)
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  // Poll messages when a chat is selected for real-time updates
  useEffect(() => {
    if (!selectedChat) return;
    
    // Fetch messages immediately when chat is selected
    const fetchCurrentMessages = async () => {
      try {
        const response = await api.get(`/chat/${selectedChat.id}/messages`);
        setMessages(prev => {
          // Only update if there are new messages
          if (response.data.length !== prev.length) {
            return response.data;
          }
          // Check if last message is different
          const lastNew = response.data[response.data.length - 1];
          const lastOld = prev[prev.length - 1];
          if (lastNew?.id !== lastOld?.id) {
            return response.data;
          }
          return prev;
        });
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };
    
    // Poll messages every 5 seconds (reduced load - WebSocket handles real-time)
    const msgInterval = setInterval(fetchCurrentMessages, 5000);
    return () => clearInterval(msgInterval);
  }, [selectedChat?.id, api]);

  const handleSelectChat = useCallback(async (chat) => {
    setSelectedChat(chat);
    await fetchMessages(chat.id);
    fetchConversations();
  }, []);

  const handleStartDirectChat = async (userId) => {
    try {
      const response = await api.post(`/chat/direct/${userId}`);
      setShowNewChatDialog(false);
      await fetchConversations();
      handleSelectChat(response.data);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      toast.error('Please enter group name and select members');
      return;
    }
    try {
      const response = await api.post('/chat/groups', {
        name: groupName,
        member_ids: selectedMembers
      });
      setShowNewGroupDialog(false);
      setGroupName('');
      setSelectedMembers([]);
      await fetchConversations();
      handleSelectChat(response.data);
      toast.success('Group created successfully');
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) return;
    try {
      await api.put(`/chat/groups/${selectedChat.id}`, { add_members: selectedMembers });
      setShowAddMemberDialog(false);
      setSelectedMembers([]);
      const response = await api.get(`/chat/${selectedChat.id}`);
      setSelectedChat(response.data);
      toast.success('Members added successfully');
    } catch (error) {
      toast.error('Failed to add members');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;
    
    // Clear typing indicator when sending
    sendTypingIndicator(false);
    
    const messageContent = newMessage.trim();
    setNewMessage(''); // Clear input immediately
    const replyToId = replyingTo?.id;
    setReplyingTo(null); // Clear reply
    
    // Keep focus on input for continuous typing - use requestAnimationFrame for reliability
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
    
    // Create optimistic message for instant UI update
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      sender_id: user.id,
      sender_name: user.name,
      created_at: new Date().toISOString(),
      reactions: [],
      reply_to: replyingTo ? {
        id: replyingTo.id,
        content: replyingTo.content?.substring(0, 100),
        sender_name: replyingTo.sender_name,
        sender_id: replyingTo.sender_id
      } : null,
      is_sending: true
    };
    
    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);
    scrollToBottom();
    
    setSendingMessage(true);
    try {
      const response = await api.post(`/chat/${selectedChat.id}/messages`, { 
        content: messageContent,
        reply_to_message_id: replyToId
      });
      // Replace optimistic message with real one
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id ? response.data : msg
      ));
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      setNewMessage(messageContent); // Restore message
      setReplyingTo(replyingTo); // Restore reply
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
      // Ensure focus is maintained after send completes - double ensure with RAF
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }
  };

  // Send typing indicator to other participants
  const sendTypingIndicator = (isTyping) => {
    if (!selectedChat || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      chat_id: selectedChat.id,
      is_typing: isTyping
    }));
    isTypingRef.current = isTyping;
  };

  // Get typing user names for current chat
  const getTypingUsersDisplay = () => {
    if (!selectedChat || !typingUsers[selectedChat.id]?.length) return null;
    
    const typingUserIds = typingUsers[selectedChat.id];
    const typingNames = [];
    
    selectedChat.participants_info?.forEach(p => {
      if (typingUserIds.includes(p.id)) {
        typingNames.push(p.name.split(' ')[0]); // First name only
      }
    });
    
    if (typingNames.length === 0) return null;
    if (typingNames.length === 1) return `${typingNames[0]} is typing...`;
    if (typingNames.length === 2) return `${typingNames[0]} and ${typingNames[1]} are typing...`;
    return `${typingNames.length} people are typing...`;
  };

  // Check if user in chat is online
  const isParticipantOnline = (participantId) => {
    return onlineUsers.has(participantId);
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
      await fetchMessages(selectedChat.id);
      setActiveReactionMsg(null);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  // Handle replying to a message
  const handleReply = (message) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  };

  // Admin: edit any message
  const handleEditMessage = async (message) => {
    const newContent = window.prompt('Edit message:', message.content);
    if (newContent === null) return;
    if (!newContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    try {
      await api.put(`/chat/messages/${message.id}`, { content: newContent.trim() });
      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, content: newContent.trim(), edited_at: new Date().toISOString() } : m));
      fetchConversations();
      toast.success('Message edited');
    } catch (error) {
      toast.error('Failed to edit message');
    }
  };

  // Admin: delete any message
  const handleDeleteMessage = async (message) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    try {
      await api.delete(`/chat/messages/${message.id}`);
      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, deleted: true } : m));
      fetchConversations();
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Insert @mention into message
  const insertMention = (user) => {
    const beforeAt = newMessage.substring(0, newMessage.lastIndexOf('@'));
    setNewMessage(`${beforeAt}@${user.first_name} `);
    setShowMentionSuggestions(false);
    setMentionSearch('');
    inputRef.current?.focus();
  };

  // Handle input change with typing indicator and @mention detection
  const handleInputChangeWithMentions = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Detect @mention being typed
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      // Check if we're in the middle of typing a mention (no space after @)
      if (!afterAt.includes(' ')) {
        setMentionSearch(afterAt.toLowerCase());
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
    
    // Send typing indicator
    if (!isTypingRef.current && e.target.value.trim()) {
      sendTypingIndicator(true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        sendTypingIndicator(false);
      }
    }, 2000);
  };

  // Get filtered users for mention suggestions
  const getMentionSuggestions = () => {
    if (!selectedChat?.participants_info) return [];
    return selectedChat.participants_info.filter(p => 
      p.id !== user?.id && 
      (p.name.toLowerCase().includes(mentionSearch) || 
       p.name.split(' ')[0].toLowerCase().startsWith(mentionSearch))
    ).slice(0, 5);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const response = await api.post(`/chat/${selectedChat.id}/messages`, {
        content: `📎 ${file.name}`,
        file_url: uploadResponse.data.file_url,
        file_name: uploadResponse.data.file_name,
        file_type: uploadResponse.data.file_type
      });
      
      setMessages(prev => [...prev, response.data]);
      scrollToBottom();
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  // getChatName is kept inside for the header and other places that need it
  const getChatName = (chat) => {
    if (chat.type === 'group') return chat.name;
    return chat.participants_info?.[0]?.name || 'Unknown';
  };

  const filteredUsers = useMemo(() => availableUsers.filter(u => 
    `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  ), [availableUsers, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7rem)] md:h-[calc(100vh-8rem)] flex flex-col md:flex-row gap-0 md:gap-4" data-testid="chat-page">
      {/* Conversations List - Hidden on mobile when chat selected */}
      <Card className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0 border-0 shadow-xl overflow-hidden flex-col h-full`}>
        <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-3 md:p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Chats
            </CardTitle>
            <div className="flex gap-1">
              <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
                <DialogTrigger asChild>
                  <Button size="icon" variant="ghost" className="hover:bg-white/20 text-white h-8 w-8">
                    <Plus className="w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Start New Conversation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
                    </div>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2">
                        {filteredUsers.map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors" onClick={() => handleStartDirectChat(u.id)}>
                            <div className="relative">
                              <Avatar>
                                <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white">
                                  {getInitials(`${u.first_name} ${u.last_name}`)}
                                </AvatarFallback>
                              </Avatar>
                              <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${u.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{u.first_name} {u.last_name}</p>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${u.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {u.is_online ? 'Online' : 'Offline'}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                            <Badge variant="secondary" className="text-xs capitalize">{u.role}</Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
                <DialogTrigger asChild>
                  <Button size="icon" variant="ghost" className="hover:bg-white/20 text-white h-8 w-8">
                    <Users className="w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Group Chat</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Input placeholder="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
                    <p className="text-sm text-muted-foreground">Select members:</p>
                    <ScrollArea className="h-[250px]">
                      <div className="space-y-2">
                        {availableUsers.map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer" onClick={() => setSelectedMembers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}>
                            <Checkbox checked={selectedMembers.includes(u.id)} />
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs">
                                {getInitials(`${u.first_name} ${u.last_name}`)}
                              </AvatarFallback>
                            </Avatar>
                            <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <Button onClick={handleCreateGroup} className="w-full">Create Group ({selectedMembers.length} members)</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        
        <ScrollArea className="flex-1 h-[calc(100%-4rem)]">
          <div className="p-2">
            {conversations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs">Start a new chat!</p>
              </div>
            ) : (
              conversations.map(chat => (
                <ConversationItem 
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedChat?.id === chat.id}
                  onClick={() => handleSelectChat(chat)}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className={`${!selectedChat ? 'hidden md:flex' : 'flex'} flex-1 flex-col border-0 shadow-xl overflow-hidden h-full`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-3 md:p-4 border-b bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
              <div className="flex items-center gap-2 md:gap-3">
                {/* Back button for mobile */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden h-8 w-8"
                  onClick={() => setSelectedChat(null)}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <Avatar className="w-8 h-8 md:w-10 md:h-10">
                    <AvatarFallback className={`${selectedChat.type === 'group' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-indigo-400 to-purple-500'} text-white text-xs md:text-sm`}>
                      {selectedChat.type === 'group' ? <Users className="w-4 h-4 md:w-5 md:h-5" /> : getInitials(getChatName(selectedChat))}
                    </AvatarFallback>
                  </Avatar>
                  {/* Online status dot in header */}
                  {selectedChat.type === 'direct' && (
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800 ${selectedChat.participants_info?.[0]?.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{getChatName(selectedChat)}</p>
                    {selectedChat.type === 'direct' && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedChat.participants_info?.[0]?.is_online ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                        {selectedChat.participants_info?.[0]?.is_online ? 'Online' : 'Offline'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {getTypingUsersDisplay() || (selectedChat.type === 'group' ? `${selectedChat.participants_info?.length || 0} members` : selectedChat.participants_info?.[0]?.role)}
                  </p>
                </div>
              </div>
              {selectedChat.type === 'group' && (
                <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon"><UserPlus className="w-5 h-5" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Members to Group</DialogTitle></DialogHeader>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2">
                        {availableUsers.filter(u => !selectedChat.participants?.includes(u.id)).map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer" onClick={() => setSelectedMembers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}>
                            <Checkbox checked={selectedMembers.includes(u.id)} />
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs">
                                {getInitials(`${u.first_name} ${u.last_name}`)}
                              </AvatarFallback>
                            </Avatar>
                            <p className="text-sm">{u.first_name} {u.last_name}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <Button onClick={handleAddMembers} disabled={selectedMembers.length === 0}>Add {selectedMembers.length} Member(s)</Button>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg, index) => {
                  const isOwn = msg.sender_id === user?.id;
                  const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;
                  
                  return (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      isOwn={isOwn}
                      showAvatar={showAvatar}
                      onReply={handleReply}
                      onReaction={handleReaction}
                      activeReactionMsg={activeReactionMsg}
                      setActiveReactionMsg={setActiveReactionMsg}
                      backendUrl={process.env.REACT_APP_BACKEND_URL}
                      isAdmin={isAdmin()}
                      onEdit={handleEditMessage}
                      onDelete={handleDeleteMessage}
                    />
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-2 md:p-4 border-t bg-background">
              {/* Reply Preview Bar */}
              {replyingTo && (
                <div className="flex items-center justify-between mb-2 px-2 md:px-3 py-2 rounded-lg bg-muted border-l-4 border-primary">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Reply className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-primary">Replying to {replyingTo.sender_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{replyingTo.content}</p>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={cancelReply}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Mention Suggestions */}
              {showMentionSuggestions && getMentionSuggestions().length > 0 && (
                <div className="absolute bottom-[calc(100%+8px)] left-2 right-2 md:left-4 md:right-4 bg-popover border rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground px-2 py-1">Suggestions</p>
                    {getMentionSuggestions().map(u => (
                      <div
                        key={u.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                        onClick={() => insertMention(u)}
                      >
                        <Avatar className="w-6 h-6">
                          <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs">
                            {getInitials(u.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{u.name}</span>
                        <span className="text-xs text-muted-foreground">@{u.name.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-1 md:gap-2">
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 md:h-10 md:w-10 flex-shrink-0" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                  {uploadingFile ? <RefreshCw className="w-4 h-4 md:w-5 md:h-5 animate-spin" /> : <Paperclip className="w-4 h-4 md:w-5 md:h-5" />}
                </Button>
                
                {/* Emoji Picker - Hidden on very small screens */}
                <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="hidden sm:flex h-9 w-9 md:h-10 md:w-10 flex-shrink-0">
                      <Smile className="w-4 h-4 md:w-5 md:h-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 md:w-80 p-0" side="top" align="start">
                    <div className="p-2 border-b">
                      <div className="flex gap-1 overflow-x-auto">
                        {Object.keys(EMOJI_CATEGORIES).map(cat => (
                          <Button key={cat} variant={selectedEmojiCategory === cat ? 'secondary' : 'ghost'} size="sm" className="text-xs whitespace-nowrap" onClick={() => setSelectedEmojiCategory(cat)}>
                            {cat}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <ScrollArea className="h-48 p-2">
                      <div className="grid grid-cols-8 gap-1">
                        {EMOJI_CATEGORIES[selectedEmojiCategory].map(emoji => (
                          <Button key={emoji} variant="ghost" size="sm" className="text-xl p-0 h-8 w-8" onClick={() => handleEmojiSelect(emoji)}>
                            {emoji}
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
                
                <textarea 
                  ref={inputRef}
                  value={newMessage} 
                  onChange={handleInputChangeWithMentions}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newMessage.trim()) {
                        handleSendMessage(e);
                      }
                    }
                  }}
                  placeholder="Type a message..." 
                  className="flex-1 min-w-0 h-9 md:h-10 text-sm md:text-base resize-none py-2 px-3 rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" 
                  disabled={sendingMessage}
                  rows={1}
                  style={{ maxHeight: '120px', overflowY: 'auto' }}
                  data-testid="chat-message-input" 
                />
                <Button type="submit" className="h-9 w-9 md:h-10 md:w-10 p-0 flex-shrink-0" disabled={sendingMessage || !newMessage.trim()} data-testid="chat-send-button">
                  {sendingMessage ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Typing Indicator */}
              {getTypingUsersDisplay() && (
                <div className="px-4 py-1 text-xs text-muted-foreground flex items-center gap-2 animate-pulse">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  {getTypingUsersDisplay()}
                </div>
              )}
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Welcome to Chat</h3>
              <p className="text-muted-foreground">Select a conversation or start a new one</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChatPage;
